import type ProformaInvoiceEntity from '../../../../db/entities/proforma_invoice/ProformaInvoiceEntity';
import type { ProformaInvoiceItemEntity } from '../../../../db/entities/proforma_invoice/ProformaInvoiceEntity';
import type ProposalEntity from '../../../../db/entities/proposal/ProposalEntity';
import type ProposalSettings from '../../../../db/entities/proposalSettings/ProposalSettingsEntity';
import type { ProformaInvoiceUpsertPayload } from '../../../../api/proformaInvoices/types';
import { DISCOUNT_TYPES } from '../../../../db/entities/proposal/ProposalDC';
import { mapProposalSettingsEntityToDC } from '../../../../db/entities/proposalSettings/mapper';

/** The invoice created from `quoteUuid`, if any. A quote has at most one invoice. */
export const findLinkedInvoice = (
  invoices: ProformaInvoiceEntity[],
  quoteUuid: string
): ProformaInvoiceEntity | undefined =>
  invoices.find((invoice) => invoice.priceProposalId === quoteUuid);

const toNumber = (value: unknown): number => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

export const mapQuoteItemsToInvoiceItems = (quote: ProposalEntity): ProformaInvoiceItemEntity[] =>
  (quote.items ?? []).map((item, index) => ({
    order: index + 1,
    description: item.description ?? '',
    qty: toNumber(item.qty),
    amount: toNumber(item.unitPrice),
  }));

const normalizeInvoiceItems = (items: ProformaInvoiceItemEntity[]) =>
  [...(items ?? [])]
    .sort((a, b) => a.order - b.order)
    .map((item, index) => ({
      order: index + 1,
      description: item.description ?? '',
      qty: toNumber(item.qty),
      amount: toNumber(item.amount),
    }));

// A zero discount is the same whatever its type.
const normalizeDiscount = (type: DISCOUNT_TYPES | null, amount: number | null) => {
  const value = toNumber(amount);
  return value ? { type: type ?? DISCOUNT_TYPES.VALUE, value } : { type: null, value: 0 };
};

/**
 * The values "Update linked invoice" copies from the quote: items and prices,
 * discount, contact, currency and taxes. Totals are not copied: the backend
 * recalculates them from these.
 */
const quoteContent = (quote: ProposalEntity) => ({
  items: mapQuoteItemsToInvoiceItems(quote),
  discount: normalizeDiscount(quote.discountType, quote.discountAmount),
  contactId: quote.contactId ?? null,
  contactName: quote.client ?? null,
  currency: quote.settings?.currency ?? null,
  includesTax: !!quote.settings?.includesTax,
  taxRate: toNumber(quote.settings?.taxRate),
});

const invoiceContent = (invoice: ProformaInvoiceEntity) => ({
  items: normalizeInvoiceItems(invoice.items),
  discount: normalizeDiscount(invoice.discountType, invoice.discountAmount),
  contactId: invoice.contactId ?? null,
  contactName: invoice.contactName ?? null,
  currency: invoice.settings?.currency ?? null,
  includesTax: !!invoice.settings?.includesTax,
  taxRate: toNumber(invoice.settings?.taxRate),
});

/** True when updating the invoice from the quote would change nothing. */
export const isInvoiceUpToDateWithQuote = (
  invoice: ProformaInvoiceEntity,
  quote: ProposalEntity
): boolean => JSON.stringify(invoiceContent(invoice)) === JSON.stringify(quoteContent(quote));

/**
 * PATCH body that copies the quote's content into its invoice. Items are always
 * sent, because the backend recalculates the totals only when items are in the
 * request. The invoice keeps its own settings (business details, footer, …);
 * only currency and taxes come from the quote.
 *
 * The API's `override_pp_fields` is not used: on PATCH it applies only when the
 * linked quote changes, not to refresh an invoice from the same quote.
 */
export const mapQuoteToInvoiceUpdatePayload = (
  invoice: ProformaInvoiceEntity,
  quote: ProposalEntity,
  fallbackSettings: ProposalSettings,
  updatedBy: string | null
): ProformaInvoiceUpsertPayload => {
  const content = quoteContent(quote);
  const settings: ProposalSettings = {
    ...(invoice.settings ?? fallbackSettings),
    currency: content.currency ?? (invoice.settings ?? fallbackSettings).currency,
    includesTax: content.includesTax,
    taxRate: content.taxRate,
  };

  return {
    uuid: invoice.uuid,
    contact_id: content.contactId,
    contact_name: content.contactName,
    discount_type: content.discount.type ?? DISCOUNT_TYPES.VALUE,
    discount_amount: content.discount.value,
    items: content.items,
    settings: mapProposalSettingsEntityToDC(settings),
    updated_by: updatedBy,
  };
};
